// RowRename — the inline rename affordance of the lifecycle pattern
// (master audit R5: "Byt namn" edits in place, no navigation).
//
// A text input that temporarily REPLACES a row's label. Enter commits,
// Escape cancels, leaving the field commits (the Finder/VS Code
// convention — an author who typed a new name and clicked away meant
// it). A blank or unchanged name cancels instead of committing, so the
// pattern can never submit a no-op or an empty title. What is submitted is
// plain text (cleanName.ts: control characters out, trimmed, at most CL-5).
// With `onBlank`, Enter on a blank name is reported there instead and the
// field stays open (the caller passes a `blankNote`, shown under it while the
// name is blank); with `maxLength`, typing stops there and a hint shows near
// it (SF-19).
//
// The draft is the input's own view state (like Tree's roving focus) —
// the OUTCOME is what reports up, via two distinct intents: onSubmit
// with the new title, or onCancel. The `select()` on mount is the one
// sanctioned DOM nudge (focus-management, same class as Tree's
// `.focus()`).

import { useLingui } from "@lingui/react/macro";
import { useCallback, useId, useRef, useState } from "react";
import { cleanName } from "./cleanName.js";
import "./RowRename.css";

export type RowRenameProps = {
    // The current title the field starts from.
    readonly initial: string;
    // Accessible name for the input (caller-owned / localised).
    readonly label: string;
    readonly onSubmit: (title: string) => void;
    readonly onCancel: () => void;
    readonly className?: string;
    // The initial text is a SUGGESTION (naming something new), so keeping
    // it submits it; by default an unchanged name cancels.
    readonly suggested?: boolean;
    readonly onBlank?: () => void;
    // Set: the field has a line under it, for the blank note or the limit hint.
    readonly maxLength?: number;
    readonly blankNote?: string;
};

export const RowRename = ({
    initial,
    label,
    onSubmit,
    onCancel,
    className,
    suggested = false,
    onBlank,
    maxLength,
    blankNote,
}: RowRenameProps) => {
    const { t } = useLingui();
    const below = useId();
    const [draft, setDraft] = useState(initial);
    // Select the text once, on mount. The ref callback must be stable: an
    // inline arrow is a new function every render, so React re-ran it after
    // each keystroke and every typed character replaced the whole field.
    const selectOnMount = useCallback(
        (el: HTMLInputElement | null) => el?.select(),
        [],
    );
    // Guards the blur that follows Enter/Escape from double-reporting.
    const settled = useRef(false);

    const commit = (enter: boolean) => {
        if (settled.current) return;
        const title = cleanName(draft);
        if (enter && title === "" && onBlank !== undefined) {
            onBlank();
            return;
        }
        settled.current = true;
        if (title === "" || (title === initial && !suggested)) onCancel();
        else onSubmit(title);
    };
    const cancel = () => {
        if (settled.current) return;
        settled.current = true;
        onCancel();
    };

    const note = cleanName(draft) === "" ? blankNote : undefined;
    // The limit shows itself only near it (SF-19).
    const line =
        note ??
        (maxLength !== undefined && draft.length >= maxLength - 20
            ? t`Up to ${maxLength} characters`
            : undefined);
    const input = (
        <input
            className={`bilda-row-rename${className ? ` ${className}` : ""}`}
            type="text"
            aria-label={label}
            aria-describedby={line === undefined ? undefined : below}
            aria-invalid={note === undefined ? undefined : true}
            maxLength={maxLength}
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
                if (e.key === "Enter") commit(true);
                else if (e.key === "Escape") cancel();
            }}
            onBlur={() => commit(false)}
            ref={selectOnMount}
        />
    );
    if (maxLength === undefined) return input;
    return (
        <span className="bilda-row-rename-field">
            {input}
            {line === undefined ? null : (
                <span
                    id={below}
                    className="bilda-row-rename-field__line"
                    data-tone={note === undefined ? "hint" : "note"}
                >
                    {line}
                </span>
            )}
        </span>
    );
};
